<script setup lang="ts">
import type { APISearchLocationItem } from '../../types'
import { onKeyStroke, ref } from '#imports'

interface Props {
  localDestination?: string
  results?: APISearchLocationItem[]
}

const props = withDefaults(defineProps<Props>(), {
  localDestination: undefined,
  results: () => [],
})

const emits = defineEmits<{
  'destination-click': [APISearchLocationItem]
}>()

const selectedIndex = ref<number | null>(null)
const resultsContainerRef = ref<HTMLElement | null>(null)

const handleKeyDown = (event: KeyboardEvent) => {
  const keyToListen = ['ArrowDown', 'ArrowUp', 'Enter']
  if (!props.results.length || !keyToListen.includes(event.key)) {
    return
  }

  if (selectedIndex.value === null) {
    selectedIndex.value = 0
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      selectedIndex.value = (selectedIndex.value + 1) % props.results.length
      break
    case 'ArrowUp':
      selectedIndex.value = (selectedIndex.value - 1 + props.results.length) % props.results.length
      break
    case 'Enter':
      if (resultsContainerRef.value) {
        resultsContainerRef.value.focus()
      }
      emits('destination-click', props.results[selectedIndex.value])
      break
  }
}

onKeyStroke(['ArrowDown', 'ArrowUp', 'Enter'], (e) => {
  handleKeyDown(e)
})

const destinationLabels = (result: APISearchLocationItem) => {
  if (!result.address) {
    return { label: result.label }
  }
  //  For example for Miami the data is in county and city is null
  //  For instance Guadeloupe is returned as state
  return {
    label: result.address.city ?? result.address.county ?? result.address.state,
    subLabel: [result.address.postalCode, result.address.state, result.address.countryName].filter(Boolean).join(', '),
  }
}
</script>

<template>
  <div class="mt-4 md:mt-0 px-4">
    <p
      v-if="(!localDestination || localDestination === '') && results && results.length"
      class="font-semibold my-2 md:ml-2"
    >
      {{ $t('data.search.recent') }}
    </p>
    <div
      v-if="results"
      ref="resultsContainerRef"
      tabindex="-1"
      role="listbox"
      @keydown.up.down.enter="handleKeyDown"
    >
      <button
        v-for="(result, index) in results"
        :key="result.locationId"
        class="flex flex-col md:p-2 w-full hover:bg-gray-50 rounded focus:border focus:shadow py-1 items-start"
        :class="{ 'shadow-[0_0_0_1px_rgb(0,0,0)]': index === selectedIndex }"
        :aria-selected="index === selectedIndex"
        role="option"
        @click.prevent.stop="emits('destination-click', result)"
        @focus="selectedIndex = index"
      >
        <div class="text-black">
          {{ destinationLabels(result).label }}
        </div>
        <div
          v-if="destinationLabels(result).subLabel"
          class="text-gray-400"
        >
          {{ destinationLabels(result).subLabel }}
        </div>
      </button>
    </div>
  </div>
</template>
